import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import AdminLayout from '../components/AdminLayout';
import Table from '../components/Table';
import Pagination from '../components/Pagination';
import toast from 'react-hot-toast';
import {
  PlusIcon,
  ArrowPathIcon,
  PencilIcon,
  TrashIcon,
  EyeIcon,
  TagIcon,
  GlobeAltIcon,
  BookOpenIcon
} from '@heroicons/react/24/outline';
import { getAdminAccessToken } from '../utils/authToken';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001';

const KnowledgeBase = () => {
  const navigate = useNavigate();
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [statusFilter, setStatusFilter] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const fetchArticles = async () => {
    try {
      setLoading(true);
      const token = getAdminAccessToken();
      
      const params = new URLSearchParams({
        page: currentPage,
        limit: 20,
        ...(statusFilter && { status: statusFilter })
      });

      const response = await axios.get(
        `${API_BASE_URL}/api/admin/kb/articles?${params}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setArticles(response.data.data?.articles || []);
      setTotalPages(response.data.data?.totalPages || 1);
    } catch (error) {
      console.error('Error fetching articles:', error);
      toast.error('Failed to fetch articles');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchArticles();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, statusFilter]);

  const handleDelete = async (articleId) => {
    try {
      const token = getAdminAccessToken();
      await axios.delete(
        `${API_BASE_URL}/api/admin/kb/articles/${articleId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Article deleted');
      setDeleteConfirm(null);
      fetchArticles();
    } catch (error) {
      console.error('Error deleting article:', error);
      toast.error('Failed to delete article');
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      draft: 'bg-gray-100 text-gray-800',
      published: 'bg-green-100 text-green-800',
      scheduled: 'bg-blue-100 text-blue-800'
    };

    return (
      <span className={`px-2 py-1 text-xs font-semibold rounded-full ${styles[status] || styles.draft}`}>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  const getLanguageBadge = (lang) => {
    const labels = {
      en: 'English',
      he: 'עברית'
    };
    const styles = lang === 'he' ? 'bg-purple-100 text-purple-800' : 'bg-blue-100 text-blue-800';

    return (
      <span className={`px-2 py-1 text-xs font-semibold rounded-full ${styles}`}>
        {labels[lang] || lang}
      </span>
    );
  };

  const columns = [
    {
      key: 'title',
      label: 'Title',
      render: (row) => (
        <div>
          <div className="font-medium text-gray-900">{row.title}</div>
          <div className="text-sm text-gray-500">/knowledge-base/{row.slug}</div>
        </div>
      )
    },
    {
      key: 'source_language',
      label: 'Language',
      render: (row) => (
        <div className="flex items-center gap-2">
          <GlobeAltIcon className="w-4 h-4 text-gray-400" />
          {getLanguageBadge(row.source_language)}
        </div>
      )
    },
    {
      key: 'status',
      label: 'Status',
      render: (row) => getStatusBadge(row.status)
    },
    {
      key: 'categories',
      label: 'Categories',
      render: (row) => (
        <div className="flex flex-wrap gap-1">
          {(row.categories || []).slice(0, 2).map((cat) => (
            <span
              key={cat.id}
              className="inline-flex items-center gap-1 px-2 py-0.5 text-xs bg-gray-100 text-gray-600 rounded"
            >
              <TagIcon className="w-3 h-3" />
              {cat.name}
            </span>
          ))}
          {(row.categories || []).length > 2 && (
            <span className="text-xs text-gray-500">
              +{row.categories.length - 2} more
            </span>
          )}
        </div>
      )
    },
    {
      key: 'view_count',
      label: 'Views',
      render: (row) => (
        <span className="text-sm text-gray-600">{row.view_count || 0}</span>
      )
    },
    {
      key: 'feedback',
      label: 'Feedback',
      render: (row) => {
        const feedback = row.feedback || { helpful: 0, notHelpful: 0, total: 0 };
        if (feedback.total === 0) {
          return <span className="text-sm text-gray-400">—</span>;
        }
        return (
          <div className="flex items-center gap-2 text-sm">
            <span className="text-green-600" title="Helpful">👍 {feedback.helpful}</span>
            <span className="text-gray-400" title="Not helpful">👎 {feedback.notHelpful}</span>
          </div>
        );
      }
    },
    {
      key: 'publish_date',
      label: 'Published',
      render: (row) => (
        <span className="text-sm text-gray-600">
          {row.publish_date
            ? new Date(row.publish_date).toLocaleDateString()
            : '—'}
        </span>
      )
    },
    {
      key: 'actions',
      label: 'Actions',
      render: (row) => (
        <div className="flex items-center gap-2">
          <button
            onClick={() => navigate(`/knowledge-base/edit/${row.id}`)}
            className="p-1 text-gray-400 hover:text-primary-600 rounded"
            title="Edit"
          >
            <PencilIcon className="w-5 h-5" />
          </button>
          {row.status === 'published' && (
            <a
              href={`${process.env.REACT_APP_CLIENT_URL || 'http://localhost:3000'}/knowledge-base/${row.slug}`}
              target="_blank"
              rel="noopener noreferrer"
              className="p-1 text-gray-400 hover:text-green-600 rounded"
              title="View"
            >
              <EyeIcon className="w-5 h-5" />
            </a>
          )}
          <button
            onClick={() => setDeleteConfirm(row.id)}
            className="p-1 text-gray-400 hover:text-red-600 rounded"
            title="Delete"
          >
            <TrashIcon className="w-5 h-5" />
          </button>
        </div>
      )
    }
  ];

  return (
    <AdminLayout>
      <div>
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
              <BookOpenIcon className="w-8 h-8 text-primary-600" />
              Knowledge Base
            </h1>
            <p className="mt-1 text-sm text-gray-500">
              Manage support guides and tutorials (auto-translated to all languages)
            </p>
          </div>
          <div className="flex items-center gap-3">
            <Link
              to="/knowledge-base/categories"
              className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 flex items-center gap-2"
            >
              <TagIcon className="w-5 h-5" />
              Categories
            </Link>
            <button
              onClick={fetchArticles}
              disabled={loading}
              className="p-2 text-gray-600 hover:text-primary-600 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50"
              title="Refresh"
            >
              <ArrowPathIcon className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
            </button>
            <Link
              to="/knowledge-base/new"
              className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 flex items-center gap-2"
            >
              <PlusIcon className="w-5 h-5" />
              New Article
            </Link>
          </div>
        </div>

        {/* Info Banner */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
          <div className="flex items-start gap-3">
            <GlobeAltIcon className="w-6 h-6 text-blue-600 flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="font-medium text-blue-900">Auto-Translation Enabled</h3>
              <p className="text-sm text-blue-700 mt-1">
                Write articles in English or Hebrew. They will be automatically translated to all supported languages 
                (Russian, Arabic, French, German, Spanish, Italian) when viewed by users.
              </p>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-lg shadow p-4 mb-6">
          <select
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value);
              setCurrentPage(1);
            }}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          >
            <option value="">All Statuses</option>
            <option value="draft">Draft</option>
            <option value="published">Published</option>
            <option value="scheduled">Scheduled</option>
          </select>
        </div>

        {/* Table */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <Table
            columns={columns}
            data={articles}
            loading={loading}
            emptyMessage="No articles found. Create your first knowledge base article!"
          />
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="mt-6">
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={setCurrentPage}
            />
          </div>
        )}

        {/* Delete Confirmation Modal */}
        {deleteConfirm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                Delete Article?
              </h3>
              <p className="text-gray-600 mb-6">
                This action cannot be undone. The article and all its translations will be permanently deleted.
              </p>
              <div className="flex justify-end gap-3">
                <button
                  onClick={() => setDeleteConfirm(null)}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={() => handleDelete(deleteConfirm)}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default KnowledgeBase;

